import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useUserActions } from 'actions/user-actions';
import { IUser } from 'common/interfaces/iuser';
import { FC, useEffect, useState } from 'react';
import { useAppSelector } from 'setup/store';
import UserStoresUpdate from './UserStoresUpdate';
import UICard from 'components/lib/card/UICard';
import { toNumber } from 'lodash';
import { IRole } from 'common/interfaces/irole';
import { formatPhoneNumberInputText, validateFormData } from 'common/utils';
import { useNavigate } from 'react-router-dom';
import { useAppActions } from 'actions/app-actions';
import { ErrorType, FormItem, SnackbarType } from 'common/interfaces';
import { UserRolePriority, UserRoleTypeEnum } from 'common/constants/user';

type UserDataFormProps = {
  userId?: string;
  readonly?: boolean;
  addNew?: boolean;
};

const DEFAULT_USER_DATA = {
  lastName: '',
  phoneNo: '',
  email: '',
} as IUser;

const FORM_FIELDS = [
  {
    itemKey: 'username',
    label: 'Username',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'firstName',
    label: 'First Name',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'lastName',
    label: 'Last Name',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'email',
    label: 'Email',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'email',
    required: true,
  },
  {
    itemKey: 'roleId',
    label: 'Role',
    type: 'dropdown',
    minLength: 1,
    maxLength: 5,
    validationType: 'numeric',
    required: true,
  },
] as FormItem[];

const UserDataForm: FC<UserDataFormProps> = ({ userId, readonly, addNew }) => {
  const roleIdMap = useAppSelector((state) => state.userReducer.roleIdMap);
  const roles = useAppSelector((state) => state.userReducer.roles);
  const error = useAppSelector((store) => store.userReducer.error);
  const updateError = useAppSelector((store) => store.updateUserReducer.error);
  const updating = useAppSelector((store) => store.updateUserReducer.loading);
  const isUpdated = useAppSelector((store) => store.updateUserReducer.isUpdated);
  const loggedInUser = useAppSelector((store) => store.appReducer.loggedInUser);

  const navigate = useNavigate();
  const [formError, setFormError] = useState<ErrorType>({});
  const [_user, setUser] = useState<IUser>();
  const [_roles, setRoles] = useState<IRole[]>([] as IRole[]);
  const [_readOnly, setReadOnly] = useState<boolean>(readonly || false);
  const { showSnackBar } = useAppActions();
  const { loadUserDetailsByUuId, loadAllRoles, updateUser, resetUpdateUserForm, setError } = useUserActions();

  const [selectedUserRole, setSelectedUserRole] = useState<IRole>();

  const _loadUser = async (userId: string) => {
    setError(undefined);
    const user = await loadUserDetailsByUuId(userId);
    if (!user || !user.data?.id) {
      setError('User not found');
    }
    setUser(user?.data);
  };
  useEffect(() => {
    console.log('Mounted storeScreen.....1', userId);
    resetUpdateUserForm();
    if (userId) {
      _loadUser(userId);
      loadAllRoles();
    } else if (addNew) {
      loadAllRoles();
      setUser(DEFAULT_USER_DATA);
    }
  }, [userId, addNew]);

  useEffect(() => {
    if (loggedInUser && roles && roleIdMap && _user) {
      let applicableRoles;
      if (_user.roleId && roleIdMap[_user.roleId].roleName === 'super_admin') {
        applicableRoles = roles.items?.filter((role) => role.roleName === 'super_admin');
        setReadOnly(true);
      } else {
        const loggedInRole = loggedInUser.roleId ? roleIdMap[loggedInUser.roleId] : undefined;
        if (loggedInRole) {
          applicableRoles = roles.items?.filter((role) =>
            loggedInRole.roleName === 'super_admin'
              ? role.priority > UserRolePriority[UserRoleTypeEnum.STORE_ADMIN] &&
                role.priority < UserRolePriority[UserRoleTypeEnum.MASTER_ADMIN]
              : role.priority < loggedInRole.priority,
          );
          if (_user.roleId && roleIdMap[_user.roleId].roleName === UserRoleTypeEnum.FRANCHISEE_ADMIN) {
            applicableRoles = roles.items?.filter((role) => role.roleName === UserRoleTypeEnum.FRANCHISEE_ADMIN);
          }
        }

        // applicableRoles = roles.items?.filter(
        //   (role) => role.pr === 'franchisee_admin' || role.roleName === 'accountant',
        // );
      }
      const _role = _user.roleId ? roleIdMap[_user.roleId] : undefined;
      if ((!applicableRoles || applicableRoles.length == 0) && _role?.id) {
        applicableRoles = [_role];
      }
      setRoles(applicableRoles || []);
      setSelectedUserRole(_role);
    }
  }, [roles, roleIdMap, _user, loggedInUser]);

  const validateForm = (itemKey?: string, value?: any) => {
    const validation = validateFormData(FORM_FIELDS, _user, itemKey, value);
    setFormError({ ...validation.error });
    return validation.valid;
  };

  const _handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (_user) {
      const isValid = validateForm();
      if (isValid) {
        const resp = await updateUser(_user);
        if (resp && userId) {
          _loadUser(userId);
          showSnackBar(SnackbarType.SUCCESS, 'Updated successfully!');
        } else if (resp) {
          setUser(DEFAULT_USER_DATA);
          showSnackBar(SnackbarType.SUCCESS, 'User added successfully! Name: ' + resp.username);
          navigate('/users');
        }
      }
    }
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    if (event.target.value && _user) {
      setUser({ ..._user, roleId: toNumber(event.target.value) });
    }
  };

  const _handleStoreUpdate = (storeIds: number[]) => {
    if (_user) {
      setUser({ ..._user, storeIds: storeIds || [] });
    }
  };

  const _handleCancel = () => {
    navigate('/users');
  };

  return (
    <div>
      {error ? <Alert severity="error"> {error.msg}</Alert> : null}
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root': { m: 1.5, width: '50ch' },
          '& .select': { m: 1.5, width: '50ch' },
          '& .selectLabel': { m: 1.5 },
          '& .MuiButton-root': { m: 1.5 },
          //   '& .MuiSelect-root': { m: 1, width: '30ch' },
        }}
        noValidate
        autoComplete="off"
      >
        {(roleIdMap && _user?.id) || (addNew && roleIdMap && _user) ? (
          <div>
            <UICard title="">
              <FormControl fullWidth>
                <TextField
                  disabled={_readOnly || !addNew}
                  required
                  id="outlined-required"
                  label="Username"
                  value={_user?.username || ''}
                  error={formError['username'] ? true : false}
                  helperText={formError['username'] ? formError['username'] : ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUser({ ..._user, username: event.target.value });
                    validateForm('username', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_readOnly}
                  id="outlined-disabled"
                  label="Firstname"
                  value={_user?.firstName || ''}
                  error={formError['firstName'] ? true : false}
                  helperText={formError['firstName'] ? formError['firstName'] : ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUser({ ..._user, firstName: event.target.value });
                    validateForm('firstName', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_readOnly}
                  id="outlined-disabled"
                  label="Lastname"
                  value={_user?.lastName || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUser({ ..._user, lastName: event.target.value });
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_readOnly}
                  id="outlined-disabled"
                  label="Email"
                  value={_user?.email || ''}
                  error={formError['email'] ? true : false}
                  helperText={formError['email'] ? formError['username'] : ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUser({ ..._user, email: event.target.value });
                    validateForm('email', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_readOnly}
                  id="outlined-disabled"
                  label="Phone"
                  type="tel"
                  value={_user?.phoneNo || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const val = event.target.value;
                    const formattedText = formatPhoneNumberInputText(val, _user?.phoneNo);
                    setUser({ ..._user, phoneNo: formattedText });
                  }}
                />
              </FormControl>
              {/* <FormControl fullWidth>
                <TextField
                  disabled={readonly || roleIdMap[_user.roleId].roleName === 'super_admin'}
                  id="outlined-disabled"
                  label="Role"
                  value={roleIdMap ? roleIdMap[_user.roleId].displayName || '' : ''}
                />
              </FormControl> */}
              <FormControl fullWidth>
                <InputLabel id="userForm_Role-select-label" className="selectLabel">
                  Role
                </InputLabel>
                <Select
                  className="select"
                  disabled={_readOnly}
                  labelId="userForm_Role-select-label"
                  id="userForm_Role-select"
                  value={(_user.roleId || '') + ''}
                  label="Role"
                  // placeholder="Select a role"

                  error={formError['roleId'] ? true : false}
                  // helperText={formError['roleId'] ? formError['roleId'] : ''}
                  onChange={handleRoleChange}
                >
                  {_roles?.map((role) => (
                    <MenuItem key={'role:' + role.id} value={role.id}>
                      {role.displayName}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    marginLeft: '28px',
                    fontWeight: '400',
                  }}
                >
                  {formError['roleId'] ? formError['roleId'] : ''}
                </Typography>
              </FormControl>
              {updateError ? <Alert severity="error">{updateError?.msg}</Alert> : null}
              {/* {isUpdated ? <Alert severity="success">{'Updated successfully!'}</Alert> : null} */}
              <Button
                disabled={_readOnly}
                variant="contained"
                onClick={_handleFormSubmit}
                style={{ marginTop: '30px' }}
              >
                Submit
              </Button>
              <Button
                disabled={updating}
                variant="contained"
                onClick={_handleCancel}
                style={{ marginTop: '30px' }}
                color="error"
              >
                Cancel
              </Button>
            </UICard>
          </div>
        ) : null}
        {userId && _user?.id ? (
          <UserStoresUpdate
            userId={_user.id}
            roleId={_user.roleId}
            styles={{ marginTop: '50px' }}
            onStoresUpdated={_handleStoreUpdate}
            franchiseeId={_user.franchiseeId}
          />
        ) : null}
      </Box>
    </div>
  );
};

export default UserDataForm;
